import {
  handleSubmitMultilingualForm,
  useCreateNewsPage,
  useGetAllNews,
  useGetNewspageFormData,
  useUpdateNewsPage,
} from "@/api/cms.ts";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import { NewsItemDrawer } from "@/components/cms/NewsItemDrawer";
import { NewsPageForm } from "@/components/cms/NewsPageForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { Button } from "@/components/ui/button.tsx";
import { Form } from "@/components/ui/form";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  cmsPageLocaleBaseSchemaShape,
  newsPageContentSchema,
  withPageNameRequiredWhenPublished,
} from "@/lib/cms/cms-types";
import {
  buildCmsPagePatchPayload,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    cmsPageLocaleBaseSchemaShape.merge(
      newsPageContentSchema.omit({
        id: true,
        news: true,
      }),
    ),
  ).optional(),
);

export type FormData = z.infer<typeof formSchema>;

const NewsCMS = () => {
  const { t, i18n } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();

  const { toast } = useToast();

  const { data: newsPageData } = useGetNewspageFormData();
  const { data: news, refetch } = useGetAllNews();

  const create = useCreateNewsPage();
  const update = useUpdateNewsPage();

  const defaultValues: FormData = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const page = newsPageData?.[lang];
        return {
          ...page?.content,
          published: page?.published ?? false,
          pageName: page?.pageName ?? "",
          ...pageThemeFromDto(page),
        };
      }, websiteLanguages),
    [newsPageData, websiteLanguages],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (
      !hasInitialized.current &&
      newsPageData != null &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      form.reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [form, defaultValues, newsPageData]);

  const onSubmit = (submittedData: FormData) => {
    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = newsPageData?.[key as SupportedLocale];

      if (!localeData) return;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        ...content
      } = localeData;
      const pageNameStr = pageName ?? "";

      const mutation = baseData?.content.id ? update : create;
      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId,
            pageTextColorMode,
            pageTextColorId,
            content,
          }),
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("newsSuccessMessage"),
              duration: 4000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("newsErrorMessage"),
              variant: "error",
              duration: 4000,
            });
          },
        },
      );
    });

    // if (CMSID) {
    //   createOrUpdateNewsPage.mutate(
    //     {
    //       cmsId: CMSID,
    //       newsPageId: newsPage?.id,
    //       pageName: nameOnglet,
    //       news: news,
    //       publishedPage: isPublished,
    //       contentFR,
    //       contentEN,
    //       pageNameEN: nameOngletEN,
    //     },
    //     {
    //       onSuccess: () => {
    //         toast({
    //           title: t("success"),
    //           description: t("newsSuccessMessageNewsPage"),
    //           duration: 4000,
    //           variant: "success",
    //         });
    //       },
    //       onError: (error) => {
    //         console.log("Error", error);
    //         toast({
    //           title: t("error"),
    //           description: t("newsErrorMessageNewsPage"),
    //           variant: "error",
    //           duration: 4000,
    //         });
    //       },
    //     },
    //   );
    // }
  };

  return (
    <CMSLayout pageName={t("cmsTabNews")} formIds={["news-page"]}>
      <Form {...form}>
        <CmsPageAppearanceConfig formId="news-page" />
        <form
          id="news-page"
          onSubmit={handleSubmitMultilingualForm(form, newsPageData, onSubmit)}
          className="flex flex-col gap-8 w-full"
        >
          <div className="flex gap-4">
            <DoubleMultilingualField
              corner={(lang) => (
                <Switch dispLabel={false} name={`${lang}.published`} />
              )}
              render={(lang) => <NewsPageForm lang={lang} />}
            />
          </div>
          <div className=" w-full flex justify-between">
            <p className="text-[#0c111d] text-base font-medium font-['Raleway'] leading-normal">
              {news?.length ?? 0} {t("publicationsLabel")}
            </p>
            <NewsItemDrawer
              refetch={refetch}
              title={t("cmsAddNewsTitle")}
              trigger={<Button variant="outline">{t("cmsAddNewsButton")}</Button>}
            />
          </div>
          {news && (
            <div className="w-full grid grid-cols-3 gap-8">
              {news.map((newsItem) => {
                const lang = i18n.language as SupportedLocale;
                const title = newsItem.title?.[lang] ?? "";
                const excerpt = newsItem.excerpt?.[lang]?.trim();
                const content = newsItem.content?.[lang] ?? "";

                return (
                  <NewsItemDrawer
                    key={newsItem.id}
                    newsItem={newsItem}
                    refetch={refetch}
                    title={t("cmsAddNewsTitle")}
                    trigger={
                      <div className="w-full min-w-0 flex flex-col justify-start items-start gap-4 cursor-pointer">
                        <img
                          className="h-48 w-full object-cover relative shrink-0 rounded-sm"
                          src={newsItem.attachments?.[0]?.mediumPublicUrl}
                          alt={title || t("newsImage")}
                        />
                        <div className="self-stretch min-w-0 w-full flex flex-col justify-start items-start gap-2">
                          <p className="text-start self-stretch text-[#090909] text-sm font-semibold font-['Instrument Sans'] leading-tight">
                            {newsItem.date
                              ? new Date(newsItem.date).toLocaleDateString(
                                  "fr-FR",
                                  {
                                    year: "numeric",
                                    day: "2-digit",
                                    month: "short",
                                  },
                                )
                              : t("unknownDate")}
                          </p>
                          <p
                            className="text-start text-[#090909] text-2xl font-semibold font-['Instrument Sans'] uppercase line-clamp-2"
                            style={{ textTransform: "uppercase" }}
                          >
                            {title}
                          </p>
                          {excerpt ? (
                            <p className="text-start self-stretch text-[#475466] text-base font-medium font-['Instrument Sans'] leading-normal">
                              {excerpt}
                            </p>
                          ) : null}
                          {content ? (
                            <div
                              className="text-start self-stretch min-w-0 text-[#475466] text-base font-normal font-['Instrument Sans'] leading-normal [&_p]:m-0 [&_h1]:m-0 [&_h2]:m-0 [&_h3]:m-0"
                              dangerouslySetInnerHTML={{ __html: content }}
                            />
                          ) : null}
                        </div>
                      </div>
                    }
                  />
                );
              })}
            </div>
          )}
        </form>
      </Form>
    </CMSLayout>
  );
}

export default NewsCMS;
